import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useEffect } from "react";
import { SEO } from "@/components/SEO";
import { Link } from "react-router-dom";

export default function Terms() {
  useEffect(() => {
    // Update meta tags for Terms of Service page
    document.title = "Terms of Service | ArqeraAI";
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", "Review ArqeraAI's Terms of Service. Learn about user responsibilities, service terms, intellectual property rights, and legal agreements governing our AI consulting services.");
    }
  }, []);

  return (
    <div className="min-h-screen bg-deep-navy text-foreground">
      <SEO 
        title="Terms of Service, ArqeraAI | Legal Agreements"
        description="Review ArqeraAI's Terms of Service. Learn about user responsibilities, service terms, intellectual property rights, and legal agreements governing our AI consulting services."
        canonical="https://arqeraai.com/terms"
        noIndex={true}
      />
      {/* Header */}
      <header className="border-b border-cyan/20 bg-deep-navy/90 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Button
            asChild
            variant="ghost"
            className="text-cyan hover:text-cyan/80 hover:bg-cyan/10"
          >
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
          Terms of Service
        </h1>
        <p className="text-light-gray mb-12">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>

        <div className="space-y-8 text-medium-gray">
          {/* Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Agreement to Terms</h2>
            <p className="leading-relaxed">
              Welcome to ArqeraAI. These Terms of Service ("Terms") govern your access to and use of our website, services, and products (collectively, the "Services"). By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our Services.
            </p>
          </section>

          {/* Definitions */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Definitions</h2>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>"Company," "we," "us," or "our"</strong> refers to ArqeraAI</li>
              <li><strong>"Services"</strong> refers to our website, AI consulting services, audit tools, and related offerings</li>
              <li><strong>"User," "you," or "your"</strong> refers to the individual or entity accessing our Services</li>
              <li><strong>"Content"</strong> refers to all information, data, text, software, or other materials</li>
              <li><strong>"Client"</strong> refers to users who have engaged our consulting or implementation services</li>
            </ul>
          </section>

          {/* Use of Services */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Use of Services</h2>
            
            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Eligibility</h3>
            <p className="leading-relaxed">
              You must be at least 18 years old and capable of forming a binding contract to use our Services. By using our Services, you represent and warrant that you meet these requirements.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Account Registration</h3>
            <p className="leading-relaxed mb-4">
              Some features of our Services may require you to create an account. You agree to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and update your information to keep it accurate</li>
              <li>Maintain the security of your account credentials</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized access</li>
            </ul>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Acceptable Use</h3>
            <p className="leading-relaxed mb-4">
              You agree not to:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe upon intellectual property rights</li>
              <li>Transmit malicious code or viruses</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with or disrupt the Services</li>
              <li>Use automated systems to access the Services without permission</li>
              <li>Impersonate others or provide false information</li>
              <li>Engage in any fraudulent or deceptive practices</li>
            </ul>
          </section>

          {/* Services and Deliverables */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Services and Deliverables</h2>
            
            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Consulting Services</h3>
            <p className="leading-relaxed">
              Our consulting services are provided on a project basis as outlined in individual service agreements. Specific deliverables, timelines, and pricing will be detailed in separate contracts or statements of work.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">AI Transformation Audit</h3>
            <p className="leading-relaxed">
              Our free AI Transformation Audit provides an assessment of your AI readiness and opportunities. The audit results are for informational purposes only and do not constitute professional advice or guarantee specific outcomes.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Service Modifications</h3>
            <p className="leading-relaxed">
              We reserve the right to modify, suspend, or discontinue any aspect of our Services at any time, with or without notice. We will not be liable for any modification, suspension, or discontinuation of the Services.
            </p>
          </section>

          {/* Intellectual Property */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Intellectual Property Rights</h2>
            
            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Our Content</h3>
            <p className="leading-relaxed">
              All content on our website, including text, graphics, logos, icons, images, audio clips, and software, is the property of ArqeraAI or its content suppliers and is protected by intellectual property laws. You may not reproduce, distribute, modify, or create derivative works without our express written permission.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Your Content</h3>
            <p className="leading-relaxed">
              When you submit content to us (through forms, audits, or consultations), you retain ownership of your content. However, you grant us a non-exclusive, worldwide, royalty-free license to use, reproduce, and analyze your content solely for the purpose of providing our Services.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Client Work Product</h3>
            <p className="leading-relaxed">
              Work product delivered as part of paid consulting engagements is governed by separate service agreements. Unless otherwise specified, clients receive full rights to custom deliverables upon final payment.
            </p>
          </section>

          {/* Payment Terms */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Payment and Billing</h2>
            
            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Fees</h3>
            <p className="leading-relaxed">
              Fees for consulting services are outlined in individual service agreements. All fees are in USD unless otherwise specified and are non-refundable except as required by law or as explicitly stated in service agreements.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Payment Methods</h3>
            <p className="leading-relaxed">
              We accept various payment methods as specified in service agreements. You agree to provide accurate payment information and authorize us to charge the specified payment method.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Late Payment</h3>
            <p className="leading-relaxed">
              Late payments may be subject to interest charges and may result in suspension of services. We reserve the right to pursue collection of outstanding balances.
            </p>
          </section>

          {/* Confidentiality */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Confidentiality</h2>
            <p className="leading-relaxed">
              We understand that you may share confidential information with us during consultations. We agree to maintain the confidentiality of such information and not disclose it to third parties, except as necessary to provide Services or as required by law. Specific confidentiality terms may be outlined in separate Non-Disclosure Agreements (NDAs).
            </p>
          </section>

          {/* Warranties and Disclaimers */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Warranties and Disclaimers</h2>
            
            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Service Warranty</h3>
            <p className="leading-relaxed">
              We will perform our Services with reasonable skill and care. However, we do not guarantee specific results or outcomes from our consulting services.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Disclaimer</h3>
            <p className="leading-relaxed mb-4">
              EXCEPT AS EXPRESSLY PROVIDED IN THESE TERMS, OUR SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Warranties of merchantability and fitness for a particular purpose</li>
              <li>Warranties that the Services will be uninterrupted or error-free</li>
              <li>Warranties that any information or advice provided will be accurate or reliable</li>
              <li>Warranties regarding third-party services or integrations</li>
            </ul>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Limitation of Liability</h2>
            <p className="leading-relaxed mb-4">
              TO THE MAXIMUM EXTENT PERMITTED BY LAW:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>We shall not be liable for any indirect, incidental, special, consequential, or punitive damages</li>
              <li>Our total liability shall not exceed the amount paid by you for Services in the 12 months preceding the claim</li>
              <li>We are not liable for losses resulting from circumstances beyond our reasonable control</li>
              <li>We are not liable for losses caused by your misuse of the Services or violation of these Terms</li>
            </ul>
          </section>

          {/* Indemnification */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Indemnification</h2>
            <p className="leading-relaxed">
              You agree to indemnify, defend, and hold harmless ArqeraAI, its officers, directors, employees, and agents from any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising from your use of the Services, violation of these Terms, or infringement of any third-party rights.
            </p>
          </section>

          {/* Termination */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Termination</h2>
            
            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Termination by You</h3>
            <p className="leading-relaxed">
              You may stop using our Services at any time. If you have an active service agreement, termination terms will be governed by that agreement.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Termination by Us</h3>
            <p className="leading-relaxed">
              We reserve the right to suspend or terminate your access to the Services at any time for any reason, including violation of these Terms. We will make reasonable efforts to provide notice, except in cases of suspected fraud or security threats.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Effect of Termination</h3>
            <p className="leading-relaxed">
              Upon termination, your right to use the Services ceases immediately. Provisions that by their nature should survive termination shall survive, including intellectual property rights, disclaimers, and limitations of liability.
            </p>
          </section>

          {/* Dispute Resolution */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Dispute Resolution</h2>
            
            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Informal Resolution</h3>
            <p className="leading-relaxed">
              If you have a dispute with us, please contact us at hello@arqeraai.com to attempt to resolve the issue informally first.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Arbitration</h3>
            <p className="leading-relaxed">
              Any disputes that cannot be resolved informally shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association, except as modified by these Terms.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Class Action Waiver</h3>
            <p className="leading-relaxed">
              You agree that any dispute resolution proceedings will be conducted only on an individual basis and not in a class, consolidated, or representative action.
            </p>
          </section>

          {/* Governing Law */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Governing Law</h2>
            <p className="leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which ArqeraAI operates, without regard to its conflict of law provisions.
            </p>
          </section>

          {/* Changes to Terms */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Changes to These Terms</h2>
            <p className="leading-relaxed">
              We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on our website and updating the "Last updated" date. Your continued use of the Services after changes are posted constitutes acceptance of the updated Terms.
            </p>
          </section>

          {/* General Provisions */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">General Provisions</h2>
            
            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Entire Agreement</h3>
            <p className="leading-relaxed">
              These Terms, together with our Privacy Policy and any service-specific agreements, constitute the entire agreement between you and ArqeraAI regarding the Services.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Severability</h3>
            <p className="leading-relaxed">
              If any provision of these Terms is found to be unenforceable, the remaining provisions will continue in full force and effect.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Waiver</h3>
            <p className="leading-relaxed">
              Our failure to enforce any provision of these Terms shall not constitute a waiver of that provision or any other provision.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Assignment</h3>
            <p className="leading-relaxed">
              You may not assign or transfer these Terms or your rights under these Terms without our prior written consent. We may assign these Terms without restriction.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-3 mt-6">Third-Party Beneficiaries</h3>
            <p className="leading-relaxed">
              These Terms do not create any third-party beneficiary rights except as expressly stated.
            </p>
          </section>

          {/* Contact Information */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">Contact Us</h2>
            <p className="leading-relaxed mb-4">
              If you have any questions or concerns about these Terms of Service, please contact us:
            </p>
            <div className="bg-cyan/5 border border-cyan/20 rounded-lg p-6 mt-4">
              <p className="text-foreground font-semibold mb-2">ArqeraAI</p>
              <p className="mb-1">Email: <a href="mailto:hello@arqeraai.com" className="text-cyan hover:underline">hello@arqeraai.com</a></p>
              <p>Website: <a href="https://arqera.ai" className="text-cyan hover:underline">https://arqera.ai</a></p>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
